import { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Calendar, Play, Printer, AlertCircle, CheckCircle2, 
    Users, FileText, Clock, ChevronRight
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import ReportWorkflowTrail from './ReportWorkflowTrail';
import ReportPeriodSelector, { getMonthEndDate } from './ReportPeriodSelector';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ARAgingReport({ customers = [], fmt, viewingStudentId, viewingStudentName, onNavigate, onOpenSource }) {
    const now = new Date();
    const initialMonth = now.getMonth() + 1;
    const initialYear = now.getFullYear();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [viewType, setViewType] = useState('detailed'); // detailed, summary
    const [periodMode, setPeriodMode] = useState('monthly');
    const [periodMonth, setPeriodMonth] = useState(initialMonth);
    const [periodYear, setPeriodYear] = useState(initialYear);
    const [asOfDate, setAsOfDate] = useState(getMonthEndDate(initialYear, initialMonth));
    const [customerId, setCustomerId] = useState('all');

    const applyPeriodMode = (mode) => {
        setPeriodMode(mode);
        if (mode === 'monthly') {
            setAsOfDate(getMonthEndDate(periodYear, periodMonth));
            return;
        }
        if (mode === 'annual') {
            setAsOfDate(`${periodYear}-12-31`);
            return;
        }
        setAsOfDate(prev => prev || getMonthEndDate(initialYear, initialMonth));
    };

    const handlePeriodMonthChange = (month) => {
        setPeriodMode('monthly');
        setPeriodMonth(month);
        setAsOfDate(getMonthEndDate(periodYear, month));
    };

    const handlePeriodYearChange = (year) => {
        setPeriodYear(year);
        setAsOfDate(periodMode === 'annual' ? `${year}-12-31` : getMonthEndDate(year, periodMonth));
    };

    const handleCustomAsOfDateChange = (value) => {
        setPeriodMode('custom');
        setAsOfDate(value);
    };

    const openArTrail = (tab, label) => {
        onNavigate?.(tab);
        toast.info(`Opening ${label}. Use customer, invoice, and date references to trace this AR Aging balance back to source work.`);
    };

    const openAgingSource = (row) => {
        const invoiceNumber = row.invoice_number || '';
        onOpenSource?.({
            source_type: 'ar_invoice',
            workspace_tab: 'arinvoices',
            record_id: row.invoice_id || '',
            record_number: invoiceNumber,
            doc_number: invoiceNumber,
            record_type: 'AR Invoice',
            source_location: 'AR Aging detailed row',
            source_impact: 'Posted invoice contributes to AR Aging, AR Summary, GL, and Trial Balance.',
            source_next_action: 'Review invoice details, posted status, due date, collections, and GL evidence before correction.',
        });
    };
    
    // Refresh AR Aging Report preview
    const handleGenerate = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const params = new URLSearchParams();
            
            params.append('as_of_date', asOfDate);
            params.append('view', viewType);
            params.append('status', 'posted');
            
            if (customerId && customerId !== 'all') {
                params.append('customer_id', customerId);
            }
            if (viewingStudentId) {
                params.append('student_id', viewingStudentId);
            }
            
            const response = await axios.get(`${API}/reports/ar-aging?${params.toString()}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            setData(response.data);
            toast.success('AR Aging preview refreshed on this report tab. It uses outstanding AR balances as of the selected date; no invoices, collections, GL, or Trial Balance records were changed.');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to refresh AR Aging preview');
        } finally {
            setLoading(false);
        }
    }, [asOfDate, viewType, customerId, viewingStudentId]);
    
    return (
        <div className="space-y-6" data-testid="ar-aging-tab">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-bold text-slate-900 font-['Manrope']">AR Aging</h2>
                    <p className="text-sm text-slate-500">
                        {viewingStudentName ? `Accounts Receivable Aging for ${viewingStudentName}` : 'Accounts Receivable Aging Report by Due Date'}
                    </p>
                </div>
            </div>
            {viewingStudentName && (
                <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900" data-testid="ar-aging-student-scope">
                    Student review scope is active. Aging buckets are calculated only from {viewingStudentName}'s posted invoices and collections.
                </div>
            )}
            <ReportWorkflowTrail
                title="AR Aging trail"
                testId="ar-aging-workflow-trail"
                scope={viewingStudentName ? `Reviewing ${viewingStudentName}` : 'Your workspace'}
                items={[
                    {
                        label: 'Source records',
                        value: 'Uses posted AR invoices minus posted collections outstanding up to the selected as-of date.'
                    },
                    {
                        label: 'Where it appears',
                        value: 'Posted invoices stay in AR Invoices, collections stay in AR Collections, and the remaining balance appears here and in AR Summary.'
                    },
                    {
                        label: 'GL / Trial Balance',
                        value: 'Read-only report. Posted AR activity already affects GL, Trial Balance, and customer receivable balances.'
                    },
                    {
                        label: 'Next safe action',
                        value: 'Preview aging, review overdue buckets, open source invoices/collections, then record collection only when payment evidence exists.'
                    }
                ]}
            />
            
            {/* Filters Panel */}
            <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-4">
                {/* View Type */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <Label className="text-sm font-medium">View Type</Label>
                        <div className="flex gap-2 mt-1">
                            <Button
                                variant={viewType === 'detailed' ? 'default' : 'outline'}
                                onClick={() => setViewType('detailed')}
                                className={viewType === 'detailed' ? 'bg-[#2F8FCB]' : ''}
                                size="sm"
                                data-testid="view-detailed-btn"
                            >
                                Detailed
                            </Button>
                            <Button
                                variant={viewType === 'summary' ? 'default' : 'outline'}
                                onClick={() => setViewType('summary')}
                                className={viewType === 'summary' ? 'bg-[#2F8FCB]' : ''}
                                size="sm"
                                data-testid="view-summary-btn"
                            >
                                Summary
                            </Button>
                        </div>
                    </div>

                    <div>
                        <Label className="text-sm">Customer</Label>
                        <Select 
                            value={customerId} 
                            onValueChange={setCustomerId}
                        >
                            <SelectTrigger className="mt-1" data-testid="customer-select">
                                <SelectValue placeholder="All Customers" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Customers</SelectItem>
                                {customers.map(c => (
                                    <SelectItem key={c.customer_id} value={c.customer_id}>
                                        {c.customer_id} - {c.customer_name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <ReportPeriodSelector
                    mode={periodMode}
                    onModeChange={applyPeriodMode}
                    month={periodMonth}
                    year={periodYear}
                    onMonthChange={handlePeriodMonthChange}
                    onYearChange={handlePeriodYearChange}
                    asOfDate={asOfDate}
                    onAsOfDateChange={handleCustomAsOfDateChange}
                    customMode="single"
                    title="AR Aging Date Coverage"
                    effectText="Aging buckets are calculated from posted AR invoices and collections outstanding as of this date."
                    testId="ar-aging-date-coverage"
                />
                
                {/* Status Indicator */}
                <div className="flex items-center gap-2 text-sm text-slate-500">
                    <Clock className="w-4 h-4" />
                    <span>Status: <strong className="text-emerald-600">POSTED</strong> transactions only</span>
                </div>
                
                {/* Buttons */}
                <div className="flex gap-3 pt-2">
                    <Button
                        onClick={handleGenerate}
                        disabled={loading}
                        className="bg-[#2F8FCB] hover:bg-[#2577a8]"
                        data-testid="ar-aging-generate-btn"
                    >
                        {loading ? (
                            <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                                Refreshing...
                            </>
                        ) : (
                            <>
                                <Play className="w-4 h-4 mr-2" /> Preview Aging
                            </>
                        )}
                    </Button>
                    <Button variant="outline" onClick={() => window.print()} disabled={!data}>
                        <Printer className="w-4 h-4 mr-2" /> Print
                    </Button>
                </div>
            </div>
            
            {/* Results */}
            {!data && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-12 text-center">
                    <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-600 mb-2">Preview AR Aging to review outstanding balances</h3>
                    <p className="text-sm text-slate-400">Shows outstanding receivables grouped by days past due date</p>
                </div>
            )}
            
            {data && (
                <div className="space-y-4">
                    {/* Report Header */}
                    <div className="bg-[#0F2A44] text-white rounded-lg p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-semibold">
                                    AR Aging - {viewType === 'detailed' ? 'Detailed' : 'By Customer'}
                                </h3>
                                <p className="text-white/70 text-sm">
                                    As of {data.as_of_date} - Posted Only
                                </p>
                            </div>
                            <div className="text-right">
                                <p className="text-xs text-white/50">Generated</p>
                                <p className="text-sm">{new Date(data.generated_at).toLocaleString()}</p>
                            </div>
                        </div>
                    </div>

                    <div className="rounded-lg border border-emerald-100 bg-emerald-50 p-4" data-testid="ar-aging-where-it-appears">
                        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                            <div className="grid grid-cols-1 gap-3 md:grid-cols-4 flex-1">
                                <div>
                                    <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">What changed</p>
                                    <p className="mt-1 text-sm text-slate-700">The aging preview was refreshed from posted AR balances only. No invoice or collection was edited.</p>
                                </div>
                                <div>
                                    <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Where to find it</p>
                                    <p className="mt-1 text-sm text-slate-700">Invoices remain in AR Invoices, payments remain in AR Collections, and customer totals reconcile to AR Summary.</p>
                                </div>
                                <div>
                                    <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Report effect</p>
                                    <p className="mt-1 text-sm text-slate-700">Draft, saved, submitted, returned, or unposted AR work is excluded until posted.</p>
                                </div>
                                <div>
                                    <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Next safe action</p>
                                    <p className="mt-1 text-sm text-slate-700">Review overdue customers, open the source invoice, then record collection only with payment evidence.</p>
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-2 shrink-0">
                                <Button type="button" variant="outline" size="sm" onClick={() => openArTrail('arinvoices', 'AR Invoices')} data-testid="ar-aging-open-invoices">
                                    AR Invoices <ChevronRight className="w-4 h-4 ml-1" />
                                </Button>
                                <Button type="button" variant="outline" size="sm" onClick={() => openArTrail('arcollections', 'AR Collections')} data-testid="ar-aging-open-collections">
                                    Collections <ChevronRight className="w-4 h-4 ml-1" />
                                </Button>
                                <Button type="button" variant="outline" size="sm" onClick={() => openArTrail('arsummary', 'AR Summary')} data-testid="ar-aging-open-summary">
                                    AR Summary <ChevronRight className="w-4 h-4 ml-1" />
                                </Button>
                            </div>
                        </div>
                    </div>
                    
                    {/* Reconciliation Warning Banner */}
                    {data.reconciliation && !data.reconciliation.matches && (
                        <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4">
                            <div className="flex items-start gap-3">
                                <div className="w-10 h-10 rounded-full bg-red-200 text-red-700 flex items-center justify-center flex-shrink-0">
                                    <AlertCircle className="w-6 h-6" />
                                </div>
                                <div className="flex-1">
                                    <h4 className="font-semibold text-red-800">Reconciliation Warning</h4>
                                    <p className="text-sm text-red-700 mt-1">{data.warning}</p>
                                    <div className="grid grid-cols-3 gap-4 mt-3 text-sm">
                                        <div>
                                            <p className="text-red-600">AR Summary Outstanding</p>
                                            <p className="font-semibold">{fmt(data.reconciliation.ar_summary_outstanding)}</p>
                                        </div>
                                        <div>
                                            <p className="text-red-600">Aging Total Outstanding</p>
                                            <p className="font-semibold">{fmt(data.reconciliation.aging_total_outstanding)}</p>
                                        </div>
                                        <div>
                                            <p className="text-red-600">Delta</p>
                                            <p className="font-semibold">{fmt(data.reconciliation.delta)}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {/* Reconciliation Success */}
                    {data.reconciliation && data.reconciliation.matches && (
                        <div className="bg-emerald-50 border-2 border-emerald-300 rounded-lg p-3">
                            <div className="flex items-center gap-3">
                                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                                <div>
                                    <span className="font-semibold text-emerald-800">Reconciliation: MATCHED</span>
                                    <span className="text-emerald-600 ml-2 text-sm">
                                        Aging Total ({fmt(data.reconciliation.aging_total_outstanding)}) = AR Summary Outstanding
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {/* Bucket Totals */}
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                        <div className="bg-white rounded-xl border border-emerald-200 p-4">
                            <p className="text-xs text-slate-500 uppercase">Current</p>
                            <p className="text-xl font-bold text-emerald-600">{fmt(data.bucket_totals.current)}</p>
                            <p className="text-[10px] text-slate-400">Not yet due</p>
                        </div>
                        <div className="bg-white rounded-xl border border-blue-200 p-4">
                            <p className="text-xs text-slate-500 uppercase">1-30 Days</p>
                            <p className="text-xl font-bold text-blue-600">{fmt(data.bucket_totals.days_1_30)}</p>
                        </div>
                        <div className="bg-white rounded-xl border border-amber-200 p-4">
                            <p className="text-xs text-slate-500 uppercase">31-60 Days</p>
                            <p className="text-xl font-bold text-amber-600">{fmt(data.bucket_totals.days_31_60)}</p>
                        </div>
                        <div className="bg-white rounded-xl border border-orange-200 p-4">
                            <p className="text-xs text-slate-500 uppercase">61-90 Days</p>
                            <p className="text-xl font-bold text-orange-600">{fmt(data.bucket_totals.days_61_90)}</p>
                        </div>
                        <div className="bg-white rounded-xl border border-red-200 p-4">
                            <p className="text-xs text-slate-500 uppercase">90+ Days</p>
                            <p className="text-xl font-bold text-red-600">{fmt(data.bucket_totals.days_90_plus)}</p>
                        </div>
                        <div className="bg-slate-800 rounded-xl p-4 text-white">
                            <p className="text-xs text-white/70 uppercase">Total Outstanding</p>
                            <p className="text-xl font-bold">{fmt(data.bucket_totals.total)}</p>
                            <p className="text-[10px] text-white/50">
                                {viewType === 'detailed' ? `${data.count} invoices` : `${data.customer_count} customers`}
                            </p>
                        </div>
                    </div>
                    
                    {/* Detailed View */}
                    {viewType === 'detailed' && data.rows && (
                        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                                    <tr>
                                        <th className="text-left px-4 py-3">Customer</th>
                                        <th className="text-left px-4 py-3">Invoice #</th>
                                        <th className="text-left px-4 py-3">Invoice Date</th>
                                        <th className="text-left px-4 py-3">Due Date</th>
                                        <th className="text-right px-4 py-3">Invoice Amt</th>
                                        <th className="text-right px-4 py-3">Paid</th>
                                        <th className="text-right px-4 py-3">Outstanding</th>
                                        <th className="text-center px-4 py-3">Days Past Due</th>
                                        <th className="text-center px-4 py-3">Bucket</th>
                                        <th className="text-center px-4 py-3">Source</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {data.rows.length === 0 ? (
                                        <tr>
                                            <td colSpan="10" className="px-4 py-8 text-center text-slate-400">
                                                No outstanding invoices found
                                            </td>
                                        </tr>
                                    ) : (
                                        data.rows.map((row, idx) => (
                                            <tr key={idx} className="hover:bg-slate-50">
                                                <td className="px-4 py-3">
                                                    <p className="font-medium">{row.customer_name}</p>
                                                    <p className="text-xs text-slate-400">{row.customer_id}</p>
                                                </td>
                                                <td className="px-4 py-3 font-mono text-sm">{row.invoice_number}</td>
                                                <td className="px-4 py-3">{row.invoice_date}</td>
                                                <td className="px-4 py-3">{row.due_date || '-'}</td>
                                                <td className="px-4 py-3 text-right font-mono">{fmt(row.invoice_amount)}</td>
                                                <td className="px-4 py-3 text-right font-mono text-emerald-600">{fmt(row.amount_paid)}</td>
                                                <td className="px-4 py-3 text-right font-mono font-semibold text-amber-600">{fmt(row.outstanding)}</td>
                                                <td className="px-4 py-3 text-center">
                                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                                        row.days_past_due <= 0 ? 'bg-emerald-100 text-emerald-700' :
                                                        row.days_past_due <= 30 ? 'bg-blue-100 text-blue-700' :
                                                        row.days_past_due <= 60 ? 'bg-amber-100 text-amber-700' :
                                                        row.days_past_due <= 90 ? 'bg-orange-100 text-orange-700' :
                                                        'bg-red-100 text-red-700'
                                                    }`}>
                                                        {row.days_past_due <= 0 ? 'Current' : `${row.days_past_due} days`}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-center">
                                                    <span className={`px-2 py-1 rounded text-xs font-bold ${
                                                        row.bucket === 'current' ? 'bg-emerald-500 text-white' :
                                                        row.bucket === 'days_1_30' ? 'bg-blue-500 text-white' :
                                                        row.bucket === 'days_31_60' ? 'bg-amber-500 text-white' :
                                                        row.bucket === 'days_61_90' ? 'bg-orange-500 text-white' :
                                                        'bg-red-500 text-white'
                                                    }`}>
                                                        {row.bucket_label}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-center">
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => openAgingSource(row)}
                                                        data-testid={`ar-aging-open-source-${row.invoice_number || idx}`}
                                                        aria-label={`Open AR Invoice ${row.invoice_number || ''} source evidence`}
                                                        title={`Open AR Invoice ${row.invoice_number || ''} source evidence`}
                                                    >
                                                        <FileText className="w-4 h-4 mr-1" /> Open Source
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                    
                    {/* Summary View */}
                    {viewType === 'summary' && data.rows && (
                        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                                    <tr>
                                        <th className="text-left px-4 py-3">Customer</th>
                                        <th className="text-center px-4 py-3"># Invoices</th>
                                        <th className="text-right px-4 py-3 bg-emerald-50">Current</th>
                                        <th className="text-right px-4 py-3 bg-blue-50">1-30 Days</th>
                                        <th className="text-right px-4 py-3 bg-amber-50">31-60 Days</th>
                                        <th className="text-right px-4 py-3 bg-orange-50">61-90 Days</th>
                                        <th className="text-right px-4 py-3 bg-red-50">90+ Days</th>
                                        <th className="text-right px-4 py-3 bg-slate-100 font-bold">Total</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {data.rows.length === 0 ? (
                                        <tr>
                                            <td colSpan="8" className="px-4 py-8 text-center text-slate-400">
                                                No outstanding balances found
                                            </td>
                                        </tr>
                                    ) : (
                                        data.rows.map((row, idx) => (
                                            <tr key={idx} className="hover:bg-slate-50">
                                                <td className="px-4 py-3">
                                                    <p className="font-medium">{row.customer_name}</p>
                                                    <p className="text-xs text-slate-400">{row.customer_id}</p>
                                                </td>
                                                <td className="px-4 py-3 text-center font-mono">{row.invoice_count}</td>
                                                <td className="px-4 py-3 text-right font-mono bg-emerald-50/50">{fmt(row.current)}</td>
                                                <td className="px-4 py-3 text-right font-mono bg-blue-50/50">{fmt(row.days_1_30)}</td>
                                                <td className="px-4 py-3 text-right font-mono bg-amber-50/50">{fmt(row.days_31_60)}</td>
                                                <td className="px-4 py-3 text-right font-mono bg-orange-50/50">{fmt(row.days_61_90)}</td>
                                                <td className="px-4 py-3 text-right font-mono bg-red-50/50">{fmt(row.days_90_plus)}</td>
                                                <td className="px-4 py-3 text-right font-mono font-bold bg-slate-100">{fmt(row.total)}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                                {/* Totals Row */}
                                <tfoot className="bg-slate-800 text-white">
                                    <tr>
                                        <td className="px-4 py-3 font-bold">TOTAL</td>
                                        <td className="px-4 py-3 text-center font-mono">{data.rows.reduce((sum, r) => sum + r.invoice_count, 0)}</td>
                                        <td className="px-4 py-3 text-right font-mono">{fmt(data.bucket_totals.current)}</td>
                                        <td className="px-4 py-3 text-right font-mono">{fmt(data.bucket_totals.days_1_30)}</td>
                                        <td className="px-4 py-3 text-right font-mono">{fmt(data.bucket_totals.days_31_60)}</td>
                                        <td className="px-4 py-3 text-right font-mono">{fmt(data.bucket_totals.days_61_90)}</td>
                                        <td className="px-4 py-3 text-right font-mono">{fmt(data.bucket_totals.days_90_plus)}</td>
                                        <td className="px-4 py-3 text-right font-mono font-bold text-lg">{fmt(data.bucket_totals.total)}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
